// Message type registry — the single source of truth for every outbound
// system message to a human (patients, therapists, leads, prospects, owners,
// ops alerts). Consumed by the server messaging router
// (functions/messagingRouter.js) and by the NiV policy screen.
//
// ⚠️ THIS FILE IS THE AUTHORED SOURCE. `functions/messageTypes.js` is generated
// from it by `node scripts/syncMessageTypes.js` (and `messageTypes.sync.test.js`
// fails the build if the two drift). Edit here, never there. Same rationale as
// smsTemplates / permissionsRegistry: functions/ is a separate CommonJS package.
//
// Channel policy (whatsapp_integration.md §0): WhatsApp is primary, SMS is the
// fallback. `channels` is the ORDERED default for a type; the first entry is
// tried first, the next only when the previous one failed / was unavailable.
// NiV can override per type at runtime (niv_settings/messaging.types[id]).
//   ["whatsapp", "sms"]  default — WhatsApp first, SMS fallback
//   ["sms"]              SMS only by design (OTPs until AUTHENTICATION templates)
//   ["sms", "whatsapp"]  SMS first (ops alerts must survive a WhatsApp outage)
// When SMS is first by design the router records `sent_sms_direct` and does
// NOT write a fallback error row — only an unexpected WhatsApp→SMS fallback
// produces CK-9265 / CK-9266.
//
// Fields:
//   id             key, also used in niv_settings/messaging.types
//   label          Hebrew label for the NiV policy table
//   channelGroup   "patients" (clinic quota applies) | "users" (CliniKit-side)
//   smsTemplateId  id in smsTemplates.js TEMPLATES / PLACEHOLDER_TEMPLATES, or
//                  null when the caller passes a pre-rendered `customBody`
//   waTemplateId   key in functions/whatsappTemplates.js, or null (no WA yet →
//                  router reports `template_missing` and falls back to SMS)
//   source         SMS_SOURCES value for quota/usage breakdown (null = none)
//   quotaExempt    true → skip checkSmsQuota (system / users-channel sends)
//   channels       ordered default channel list (see above)
//   phase          rollout phase in whatsapp_integration.md §5
//   noErrorRow     true → the router never writes a niv_error_logs row for this
//                  type (ops alerts: a row would re-trigger the alert path → loop)
//   defaultEnabled false → the type is OFF until NiV flips `types[id].enabled`
//                  on (Phase 4 automations ship dark; the router answers
//                  `skipped_disabled_by_niv` until then)
//
// The file must stay dependency-free so the generated CJS copy can be
// required from Cloud Functions with no bundling.

export const MESSAGE_CHANNELS = Object.freeze({
  WHATSAPP: "whatsapp",
  SMS: "sms",
});

export const MESSAGE_CHANNEL_LIST = Object.freeze(Object.values(MESSAGE_CHANNELS));

export const CHANNEL_GROUPS = Object.freeze({
  PATIENTS: "patients",
  USERS: "users",
});

export const DEFAULT_CHANNELS = Object.freeze(["whatsapp", "sms"]);
export const SMS_ONLY_CHANNELS = Object.freeze(["sms"]);
export const SMS_FIRST_CHANNELS = Object.freeze(["sms", "whatsapp"]);

const type = (id, def) => ({
  id,
  quotaExempt: false,
  channels: DEFAULT_CHANNELS,
  ...def,
});

export const MESSAGE_TYPES = Object.freeze({
  // ---------------------------------------------------------------- patients
  appointment_initial: type("appointment_initial", {
    label: "פגישה חדשה + קישור אישור",
    channelGroup: "patients",
    smsTemplateId: "appointment.initial",
    waTemplateId: "APPT_INITIAL",
    source: "appointment",
    phase: 2,
  }),
  appointment_initial_video: type("appointment_initial_video", {
    label: "פגישת וידאו חדשה + קישור",
    channelGroup: "patients",
    smsTemplateId: "appointment.initial",
    waTemplateId: "APPT_INITIAL_VIDEO",
    source: "appointment",
    phase: 2,
  }),
  appointment_reminder: type("appointment_reminder", {
    label: "תזכורת לפגישה",
    channelGroup: "patients",
    smsTemplateId: "appointment.reminder",
    waTemplateId: "APPT_REMINDER",
    source: "reminder",
    phase: 2,
  }),
  appointment_reminder_tomorrow: type("appointment_reminder_tomorrow", {
    label: "תזכורת לפגישה מחר (סדרה)",
    channelGroup: "patients",
    smsTemplateId: "appointment.recurringReminder",
    waTemplateId: "APPT_REMINDER_TOMORROW",
    source: "recurringReminder",
    phase: 2,
  }),
  appointment_series: type("appointment_series", {
    label: "הודעת סדרת פגישות",
    channelGroup: "patients",
    smsTemplateId: "appointment.series",
    waTemplateId: "APPT_SERIES",
    source: "appointment",
    phase: 2,
  }),
  appointment_series_video: type("appointment_series_video", {
    label: "סדרת פגישות וידאו",
    channelGroup: "patients",
    smsTemplateId: "appointment.seriesVideoLinkMessage",
    waTemplateId: "APPT_SERIES_VIDEO",
    source: "appointment",
    phase: 2,
  }),
  appointment_rescheduled: type("appointment_rescheduled", {
    label: "עדכון מועד פגישה",
    channelGroup: "patients",
    smsTemplateId: "appointment.reschedule",
    waTemplateId: "APPT_RESCHEDULED",
    source: "appointment",
    phase: 2,
  }),
  appointment_video_link: type("appointment_video_link", {
    label: "קישור לפגישת וידאו",
    channelGroup: "patients",
    smsTemplateId: "appointment.videoLinkMessage",
    waTemplateId: "APPT_VIDEO_LINK",
    source: "appointment",
    phase: 2,
  }),
  end_meeting_simple: type("end_meeting_simple", {
    label: "הודעת סיום פגישה",
    channelGroup: "patients",
    smsTemplateId: null,
    waTemplateId: "END_MEETING_SIMPLE",
    source: "endMeeting",
    phase: 3,
  }),
  end_meeting_payment: type("end_meeting_payment", {
    label: "סיום פגישה + קישור תשלום",
    channelGroup: "patients",
    smsTemplateId: null,
    waTemplateId: "END_MEETING_PAYMENT",
    source: "endMeeting",
    phase: 3,
  }),
  end_meeting_rate: type("end_meeting_rate", {
    label: "סיום פגישה + בקשת דירוג",
    channelGroup: "patients",
    smsTemplateId: null,
    waTemplateId: "END_MEETING_RATE",
    source: "endMeeting",
    phase: 3,
  }),
  payment_reminder: type("payment_reminder", {
    label: "תזכורת תשלום",
    channelGroup: "patients",
    smsTemplateId: "payment.reminder",
    waTemplateId: "PAYMENT_REMINDER",
    source: "appointment",
    phase: 3,
  }),
  payment_summary: type("payment_summary", {
    label: "סיכום תשלומים ממתינים",
    channelGroup: "patients",
    smsTemplateId: "payment.summary.default",
    waTemplateId: "PAYMENT_SUMMARY",
    source: "paymentSummary",
    phase: 3,
  }),
  // ---------------------------------------------------------- Grow payments
  // grow_integration.md Phase 6. Link in the message is OUR short URL
  // (clinikit.co.il/pay/<code>) which resolves to the Grow hosted page — so the
  // WhatsApp URL button has a fixed domain and we can record "opened".
  grow_payment_request: type("grow_payment_request", {
    label: "דרישת תשלום Grow (קישור לתשלום)",
    channelGroup: "patients",
    smsTemplateId: "grow.paymentRequest",
    waTemplateId: "GROW_PAYMENT_REQUEST",
    source: "paymentSummary",
    phase: 5,
  }),
  grow_payment_reminder: type("grow_payment_reminder", {
    label: "תזכורת דרישת תשלום Grow (לא שולם)",
    channelGroup: "patients",
    smsTemplateId: "grow.paymentReminder",
    waTemplateId: "GROW_PAYMENT_REMINDER",
    source: "paymentSummary",
    defaultEnabled: false,
    phase: 5,
  }),
  grow_payment_receipt: type("grow_payment_receipt", {
    label: "קבלה על תשלום Grow",
    channelGroup: "patients",
    smsTemplateId: "grow.paymentReceipt",
    waTemplateId: "GROW_PAYMENT_RECEIPT",
    source: "paymentSummary",
    phase: 5,
  }),
  signature_request: type("signature_request", {
    label: "טופס לחתימה דיגיטלית",
    channelGroup: "patients",
    smsTemplateId: "signature.request",
    waTemplateId: "SIGNATURE_REQUEST",
    source: "digitalSignature",
    phase: 3,
  }),
  questionnaire_request: type("questionnaire_request", {
    label: "שאלון למילוי",
    channelGroup: "patients",
    smsTemplateId: "questionnaire.request",
    waTemplateId: "QUESTIONNAIRE_REQUEST",
    source: "questionnaire",
    phase: 3,
  }),
  nutrition_menu_link: type("nutrition_menu_link", {
    label: "קישור לעמוד תזונה אישי",
    channelGroup: "patients",
    smsTemplateId: "nutrition.menuLink",
    waTemplateId: "NUTRITION_MENU_LINK",
    source: "nutritionLink",
    phase: 3,
  }),
  nutrition_measure: type("nutrition_measure", {
    label: "תזכורת מדידה (תזונה)",
    channelGroup: "patients",
    smsTemplateId: "nutrition.measurementReminder",
    waTemplateId: "NUTRITION_MEASURE",
    source: "nutritionLink",
    phase: 3,
  }),
  booking_otp: type("booking_otp", {
    label: "קוד אימות לקביעת תור",
    channelGroup: "patients",
    smsTemplateId: null,
    waTemplateId: null,
    source: null,
    quotaExempt: true,
    channels: SMS_ONLY_CHANNELS,
    phase: 5,
  }),
  booking_approved: type("booking_approved", {
    label: "תור אושר",
    channelGroup: "patients",
    smsTemplateId: "booking.approved",
    waTemplateId: "BOOKING_APPROVED",
    source: "appointment",
    phase: 3,
  }),
  booking_rejected: type("booking_rejected", {
    label: "תור לא אושר",
    channelGroup: "patients",
    smsTemplateId: "booking.rejected",
    waTemplateId: "BOOKING_REJECTED",
    source: "appointment",
    phase: 3,
  }),
  appointment_cancelled: type("appointment_cancelled", {
    label: "ביטול פגישה על ידי המטפל",
    channelGroup: "patients",
    smsTemplateId: "appointment.cancelled",
    waTemplateId: "APPT_CANCELLED",
    source: "cancellation",
    defaultEnabled: false,
    phase: 4,
  }),
  booking_received: type("booking_received", {
    label: "בקשת תור התקבלה",
    channelGroup: "patients",
    smsTemplateId: "booking.received",
    waTemplateId: "BOOKING_RECEIVED",
    source: "appointment",
    defaultEnabled: false,
    phase: 4,
  }),
  appointment_unconfirmed_nudge: type("appointment_unconfirmed_nudge", {
    label: "תזכורת אישור הגעה (לא אושר)",
    channelGroup: "patients",
    smsTemplateId: "appointment.unconfirmedNudge",
    waTemplateId: "APPT_UNCONFIRMED_NUDGE",
    source: "reminder",
    defaultEnabled: false,
    phase: 4,
  }),
  no_show_followup: type("no_show_followup", {
    label: "מעקב אי-הגעה",
    channelGroup: "patients",
    smsTemplateId: "appointment.noShowFollowup",
    waTemplateId: "NO_SHOW_FOLLOWUP",
    source: "noShowFollowup",
    defaultEnabled: false,
    phase: 4,
  }),

  // ---------------------------------------------------------- users (ניב)
  // Therapist alerts: SMS-only by design until a UTILITY template for staff
  // exists (no WA template → would otherwise log CK-9266 on every send).
  therapist_declined_alert: type("therapist_declined_alert", {
    label: "התראה למטפל: מטופל ביטל",
    channelGroup: "users",
    smsTemplateId: null,
    waTemplateId: null,
    source: "therapistCancellationAlert",
    channels: SMS_ONLY_CHANNELS,
    phase: 3,
  }),
  therapist_booking_alert: type("therapist_booking_alert", {
    label: "התראה למטפל: בקשת תור",
    channelGroup: "users",
    smsTemplateId: null,
    waTemplateId: null,
    source: "bookingRequestAlert",
    channels: SMS_ONLY_CHANNELS,
    phase: 3,
  }),
  team_invite: type("team_invite", {
    label: "הזמנה לצוות",
    channelGroup: "users",
    smsTemplateId: "team.invite",
    waTemplateId: "TEAM_INVITE",
    source: "teamInvite",
    quotaExempt: true,
    phase: 3,
  }),
  signup_otp: type("signup_otp", {
    label: "קוד אימות להרשמה",
    channelGroup: "users",
    smsTemplateId: null,
    waTemplateId: null,
    source: null,
    quotaExempt: true,
    channels: SMS_ONLY_CHANNELS,
    phase: 5,
  }),
  lead_welcome: type("lead_welcome", {
    label: "ברוכים הבאים לליד",
    channelGroup: "users",
    smsTemplateId: "lead.welcome",
    waTemplateId: "LEAD_WELCOME",
    source: null,
    quotaExempt: true,
    phase: 3,
  }),
  niv_alert: type("niv_alert", {
    label: "התראת מערכת לניב",
    channelGroup: "users",
    smsTemplateId: null,
    waTemplateId: "NIV_ALERT",
    source: null,
    quotaExempt: true,
    channels: SMS_FIRST_CHANNELS,
    noErrorRow: true,
    phase: 3,
  }),
  owner_welcome: type("owner_welcome", {
    label: "ברוכים הבאים לבעל קליניקה",
    channelGroup: "users",
    smsTemplateId: "owner.welcome",
    waTemplateId: "OWNER_WELCOME",
    source: null,
    quotaExempt: true,
    defaultEnabled: false,
    phase: 4,
  }),
  owner_trial_7: type("owner_trial_7", {
    label: "ניסיון: נותרו 7 ימים",
    channelGroup: "users",
    smsTemplateId: "owner.trial7",
    waTemplateId: "OWNER_TRIAL_7",
    source: null,
    quotaExempt: true,
    defaultEnabled: false,
    phase: 4,
  }),
  owner_trial_1: type("owner_trial_1", {
    label: "ניסיון: נותר יום אחד",
    channelGroup: "users",
    smsTemplateId: "owner.trial1",
    waTemplateId: "OWNER_TRIAL_1",
    source: null,
    quotaExempt: true,
    defaultEnabled: false,
    phase: 4,
  }),
  owner_trial_expired: type("owner_trial_expired", {
    label: "ניסיון הסתיים",
    channelGroup: "users",
    smsTemplateId: "owner.trialExpired",
    waTemplateId: "OWNER_TRIAL_EXPIRED",
    source: null,
    quotaExempt: true,
    defaultEnabled: false,
    phase: 4,
  }),
  owner_plan_reverted: type("owner_plan_reverted", {
    label: "מנוי הסתיים — חזרה לניסיון",
    channelGroup: "users",
    smsTemplateId: "owner.planReverted",
    waTemplateId: "OWNER_PLAN_REVERTED",
    source: null,
    quotaExempt: true,
    defaultEnabled: false,
    phase: 4,
  }),
  lead_followup_1: type("lead_followup_1", {
    label: "מעקב ליד 1",
    channelGroup: "users",
    smsTemplateId: "lead.followup1",
    waTemplateId: "LEAD_FOLLOWUP_1",
    source: null,
    quotaExempt: true,
    defaultEnabled: false,
    phase: 4,
  }),
  lead_followup_2: type("lead_followup_2", {
    label: "מעקב ליד 2",
    channelGroup: "users",
    smsTemplateId: "lead.followup2",
    waTemplateId: "LEAD_FOLLOWUP_2",
    source: null,
    quotaExempt: true,
    defaultEnabled: false,
    phase: 4,
  }),
  prospect_approved: type("prospect_approved", {
    label: "מועמד אושר להרשמה",
    channelGroup: "users",
    smsTemplateId: "prospect.approved",
    waTemplateId: "PROSPECT_APPROVED",
    source: null,
    quotaExempt: true,
    defaultEnabled: false,
    phase: 4,
  }),
  prospect_reg_nudge: type("prospect_reg_nudge", {
    label: "תזכורת השלמת הרשמה",
    channelGroup: "users",
    smsTemplateId: "prospect.regNudge",
    waTemplateId: "PROSPECT_REG_NUDGE",
    source: null,
    quotaExempt: true,
    defaultEnabled: false,
    phase: 4,
  }),
});

export const MESSAGE_TYPE_IDS = Object.freeze(Object.keys(MESSAGE_TYPES));

/** Registry entry or null — the router treats null as a hard caller bug. */
export const getMessageType = (id) => MESSAGE_TYPES[id] || null;

/** True when the ordered list is a valid channel order (non-empty, known, unique). */
export const isValidChannelOrder = (channels) =>
  Array.isArray(channels) &&
  channels.length > 0 &&
  channels.every((c) => MESSAGE_CHANNEL_LIST.includes(c)) &&
  new Set(channels).size === channels.length;
